import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { format_weekdays } from 'console/shared/device.acl/utils';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, IconButton, InputAdornment, Link, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, } from '@mui/material';
import { ArrowDropDown, Close } from '@mui/icons-material';
import { preventDefault, useBox, useRefAnchor } from 'shared/hooks/utils';
import { MaterialBoolControl, MaterialDropdown } from 'shared/components/fields';
import { ControlWithoutLabel } from 'shared/components/fields/control/without-label';
import { noop } from 'ts-essentials';
import { days, weekends, workdays } from 'z3k-config';
import * as lodash from 'lodash';
import { pad0 } from 'utils';
import { TextInputWrapper } from 'shared/components/fields/text';
const _workdays = workdays.map((d) => d.id);
const _weekends = weekends.map((d) => d.id);
const hour_options = new Array(25)
    .fill(0)
    .map((_, i) => ({ value: i, text: pad0(i, 2) + ':00' }));
export function WeekSchedule(props) {
    const { value, onChange, readonly } = props;
    const boxedWeekRows = useBox(value);
    const week_schedule_enabled = useMemo(() => value.length > 0, [value]);
    const toggleWeekSchedule = useCallback((checked) => {
        if (readonly) {
            return;
        }
        if (!checked) {
            onChange([]);
        }
        else {
            onChange([{ days: [0, 1, 2, 3, 4], hours: [[8, 18]] }]);
        }
    }, [readonly]);
    const [anchorEl, anchorRef, onOpenDayPopup, onCloseDayPopup] = useRefAnchor();
    const day_field = useCallback((input_props) => (_jsx(TextInputWrapper, { ...input_props, endAdornment: _jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { size: "small", children: _jsx(ArrowDropDown, {}) }) }), disabled: readonly })), [readonly]);
    const [copy_2_days, setCopyToDays] = useState([]);
    const [changed_row_index, setChangedRowIndex] = useState(-1);
    const onOpenPopup = useCallback((row_index) => () => {
        onOpenDayPopup();
        const days = boxedWeekRows.current[row_index]?.days ?? [];
        setCopyToDays(days);
        setChangedRowIndex(row_index);
    }, []);
    const onClosePopup = useCallback(() => {
        onCloseDayPopup();
        onChange(boxedWeekRows.current.map((row, index) => {
            if (index === changed_row_index) {
                return { ...row, days: copy_2_days };
            }
            return row;
        }));
    }, [onCloseDayPopup, changed_row_index, copy_2_days]);
    const onChangeDays = useCallback((day_id) => (e) => {
        if (e.target.checked) {
            setCopyToDays((days) => [...days, day_id]);
        }
        else {
            setCopyToDays((days) => days.filter((_day_id) => _day_id !== day_id));
        }
    }, [setCopyToDays]);
    const onChangeWeekDays = useCallback((e) => {
        if (e.target.checked) {
            setCopyToDays((days) => [...new Set([...days, ..._workdays])]);
        }
        else {
            setCopyToDays((days) => days.filter((_day_id) => !_workdays.includes(_day_id)));
        }
    }, [setCopyToDays]);
    const onChangeWeekends = useCallback((e) => {
        if (e.target.checked) {
            setCopyToDays((days) => [...new Set([...days, ..._weekends])]);
        }
        else {
            setCopyToDays((days) => days.filter((_day_id) => !_weekends.includes(_day_id)));
        }
    }, [setCopyToDays]);
    const onAddNewRow = useCallback((e) => {
        preventDefault(e);
        onChange([
            ...boxedWeekRows.current,
            {
                days: boxedWeekRows.current[boxedWeekRows.current.length - 1]?.days || [
                    0, 1, 2, 3, 4,
                ],
                hours: [[8, 18]],
            },
        ]);
    }, []);
    const onDeleteRow = useCallback((row_index) => () => {
        onChange(boxedWeekRows.current.filter((_, i) => i !== row_index));
    }, []);
    const hour_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: hour_options, disabled: readonly })), [hour_options, readonly]);
    const onChangeFrom = useCallback((row_index) => (num_of_hour) => {
        onChange(boxedWeekRows.current.map((row, index) => {
            if (index === row_index) {
                return { ...row, hours: [[num_of_hour, row.hours[0][1]]] };
            }
            return row;
        }));
    }, []);
    const onChangeTo = useCallback((row_index) => (num_of_hour) => {
        onChange(boxedWeekRows.current.map((row, index) => {
            if (index === row_index) {
                return { ...row, hours: [[row.hours[0][0], num_of_hour]] };
            }
            return row;
        }));
    }, []);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", children: [_jsx(MaterialBoolControl, { color: "secondary", label: "\u0414\u043E\u0441\u0442\u0443\u043F \u043F\u043E \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044E", value: week_schedule_enabled, onChange: toggleWeekSchedule, disabled: readonly }), week_schedule_enabled && (_jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { variant: "head", children: _jsx("strong", { children: "\u0414\u043D\u0438 \u043D\u0435\u0434\u0435\u043B\u0438" }) }), _jsx(TableCell, { variant: "head", children: _jsx("strong", { children: "\u0441" }) }), _jsx(TableCell, { variant: "head", children: _jsx("strong", { children: "\u043F\u043E" }) }), _jsx(TableCell, { variant: "head" })] }) }), _jsxs(TableBody, { children: [value.map(({ days, hours }, row_no) => (_jsxs(TableRow, { children: [_jsx(TableCell, { onClick: onOpenPopup(row_no), ref: anchorRef, children: _jsx(ControlWithoutLabel, { value: format_weekdays(days) || '\xa0', onChange: noop, validation_result: null, validation_warnings_result: null, field: day_field }) }), _jsx(TableCell, { children: _jsx(ControlWithoutLabel, { value: hours[0][0], onChange: onChangeFrom(row_no), validation_result: null, validation_warnings_result: null, field: hour_field }) }), _jsx(TableCell, { children: _jsx(ControlWithoutLabel, { value: hours[0][1] ?? 0, onChange: onChangeTo(row_no), validation_result: null, validation_warnings_result: null, field: hour_field }) }), _jsx(TableCell, { children: _jsx(IconButton, { size: "small", color: "secondary", onClick: onDeleteRow(row_no), children: _jsx(Close, { fontSize: "small" }) }) })] }, row_no))), _jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, onClick: onAddNewRow, children: _jsx(Link, { href: "#", children: "+\u00A0\u0434\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0441\u0442\u0440\u043E\u0447\u043A\u0443" }) }) })] })] }), _jsxs(Popover, { open: !!anchorEl, anchorEl: anchorEl, onClose: onClosePopup, anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        }, transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        }, disableRestoreFocus: true, children: [_jsxs(Box, { ml: 1, children: [_jsxs(FormGroup, { row: true, children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { color: "primary", checked: lodash.isEqual(copy_2_days
                                                        .sort()
                                                        .filter((_day) => _workdays.includes(_day)), _workdays), indeterminate: copy_2_days.some((_day) => _workdays.includes(_day)) &&
                                                        !lodash.isEqual(copy_2_days
                                                            .sort()
                                                            .filter((_day) => _workdays.includes(_day)), _workdays), onChange: onChangeWeekDays }), label: "\u0411\u0443\u0434\u043D\u0438" }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { color: "primary", checked: lodash.isEqual(copy_2_days
                                                        .sort()
                                                        .filter((_day) => _weekends.includes(_day)), _weekends), indeterminate: copy_2_days.some((_day) => _weekends.includes(_day)) &&
                                                        !lodash.isEqual(copy_2_days
                                                            .sort()
                                                            .filter((_day) => _weekends.includes(_day)), _weekends), onChange: onChangeWeekends }), label: "\u0412\u044B\u0445\u043E\u0434\u043D\u044B\u0435" })] }), _jsx(FormGroup, { row: true, children: days.map((_day) => (_jsx(FormControlLabel, { control: _jsx(Checkbox, { color: "primary", checked: copy_2_days.includes(_day.id), onChange: onChangeDays(_day.id) }), label: _day.name }, _day.id))) })] }), _jsx(Box, { display: "flex", justifyContent: "flex-end", children: _jsx(Button, { color: "primary", onClick: onClosePopup, children: "\u041E\u043A" }) })] })] }))] }));
}
